import Difficulty from 'app/frontend/types/custom/Difficulty'
import GameVersion, {
  gameVersionHas,
} from 'app/frontend/types/custom/GameVersion'
import { CharacterRole } from 'app/frontend/types/custom'
import { resolveEventParticipantEntries } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityParticipants'
import { EventActivityParticipantEntry } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityParticipantEntry'

export const matchesDefaultSetup = (
  entry: wowaudit.DefaultSetup,
  encounterId: number,
  difficulty: Difficulty,
) => entry.encounterId === encounterId && entry.difficulty === difficulty

export const raidVisibleMembers = (members: wowaudit.TeamMembership[]) =>
  members.filter(
    (member) =>
      member.teamRank.raid_visibility && member.teamRank.player_type !== 'alt',
  )

export const resolveDefaultSetupRaidSizes = (
  instance: wowaudit.Instance | undefined,
  difficulty: Difficulty,
  gameVersion: GameVersion,
) => {
  const flexible =
    difficulty !== 'mythic' && gameVersionHas('flexible_raid_size', gameVersion)
  return {
    raidSize: flexible ? 10 : instance?.raidSize ?? 25,
    maxRaidSize: flexible ? 30 : instance?.maxRaidSize ?? null,
  }
}

export const buildDefaultSetupActivity = ({
  encounter,
  instance,
  difficulty,
  gameVersion,
  order = 0,
}: {
  encounter: wowaudit.Encounter
  instance?: wowaudit.Instance
  difficulty: Difficulty
  gameVersion: GameVersion
  order?: number
}): wowaudit.EventActivity => {
  const { raidSize, maxRaidSize } = resolveDefaultSetupRaidSizes(
    instance,
    difficulty,
    gameVersion,
  )

  return {
    id: encounter.id,
    active: true,
    activityType: 'raids',
    difficulty,
    encounter,
    instance,
    linkedPlans: [],
    maxRaidSize,
    order,
    planOverrides: {},
    raidSize,
  }
}

export const buildDefaultSetupEntries = ({
  team,
  members,
  activityId,
  selections,
}: {
  team: wowaudit.Team
  members: wowaudit.TeamMembership[]
  activityId: number
  selections: wowaudit.DefaultSetup['selections']
}): EventActivityParticipantEntry[] => {
  const visibleMembers = raidVisibleMembers(members)
  const participants = visibleMembers.map((member) => {
    const character = member.characterReference
    const defaultSelected =
      (member.teamRank.default_selection_status ||
        team.defaultSelectionStatus) === 'selected'
    const override = selections?.[character.id.toString()] ?? {}

    return {
      id: character.id,
      addedAsGuest: false,
      characterId: character.id,
      characterReference: character,
      effectiveDefaultSelectionStatus: defaultSelected,
      primaryCharacterReference: character,
      role: member.role as CharacterRole,
      selections: {
        [activityId.toString()]: override,
      },
      specId: member.specId ?? undefined,
      status: 'present',
      teamMembership: member,
    } as wowaudit.EventParticipant
  })

  return resolveEventParticipantEntries({
    participants,
    activityIds: [activityId],
    teamMembers: members,
  })
}

export const buildDefaultSetupGridEntries = ({
  team,
  members,
  activities,
  setups,
  difficulty,
}: {
  team: wowaudit.Team
  members: wowaudit.TeamMembership[]
  activities: wowaudit.EventActivity[]
  setups: wowaudit.DefaultSetup[] | undefined
  difficulty: Difficulty
}): EventActivityParticipantEntry[] => {
  const visibleMembers = raidVisibleMembers(members)
  const activityIds = activities.map((activity) => activity.id)
  const setupsByEncounterId = new Map(
    (setups ?? [])
      .filter((setup) => setup.difficulty === difficulty)
      .map((setup) => [setup.encounterId, setup]),
  )

  const participants = visibleMembers.map((member) => {
    const character = member.characterReference
    const defaultSelected =
      (member.teamRank.default_selection_status ||
        team.defaultSelectionStatus) === 'selected'
    const selections = Object.fromEntries(
      activities.map((activity) => {
        const encounterId = activity.encounter?.id ?? activity.id
        const setup = setupsByEncounterId.get(encounterId)
        const override = setup?.selections?.[character.id.toString()] ?? {}
        return [activity.id.toString(), override]
      }),
    )

    return {
      id: character.id,
      addedAsGuest: false,
      characterId: character.id,
      characterReference: character,
      effectiveDefaultSelectionStatus: defaultSelected,
      primaryCharacterReference: character,
      role: member.role as CharacterRole,
      selections,
      specId: member.specId ?? undefined,
      status: 'present',
      teamMembership: member,
    } as wowaudit.EventParticipant
  })

  return resolveEventParticipantEntries({
    participants,
    activityIds,
    teamMembers: members,
  })
}

export const forceSelectedOnEntries = (
  entries: EventActivityParticipantEntry[],
  activityId: number,
): EventActivityParticipantEntry[] =>
  entries.map((entry) => ({
    ...entry,
    selections: {
      ...entry.selections,
      [activityId]: {
        ...entry.selections[activityId],
        selected: true,
      },
    },
  }))
